import React, { useEffect, useState } from 'react';
import { Mail, Linkedin, Save, FileText, Upload, Trash2, ExternalLink } from 'lucide-react';
import { fetchContact, updateContact, uploadPortfolio, removePortfolio, fileUrl } from '../lib/api';
import { ActionBtn } from '../components/ReorderControls';
import SyncPanel from './SyncPanel';

const fieldCls = 'mt-1 w-full bg-[#1a0e05] border border-[#3a2512] text-[#ffe9b8] font-body text-sm px-3 py-2 rounded';
const broadcast = (data) => window.dispatchEvent(new CustomEvent('contact-updated', { detail: data }));
const errText = (err) => err?.response?.data?.detail || 'Save failed';

function Status({ msg, testId }) {
  if (!msg) return null;
  return (
    <div data-testid={testId} role="status"
         className={`font-osd text-xs px-3 py-2 rounded border ${msg.type === 'ok' ? 'text-[#9dd68a] border-[#9dd68a]/50 bg-[#0e1a09]' : 'text-[#ff6a3a] border-[#ff6a3a]/50 bg-[#1a0e05]'}`}>
      {msg.text}
    </div>
  );
}

export default function ContactEditor() {
  const [contact, setContact] = useState(null);
  useEffect(() => { fetchContact().then(setContact).catch(() => setContact({ email: '', linkedin: '' })); }, []);
  const onSaved = (data) => { setContact(data); broadcast(data); };
  if (!contact) return null;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 [&>*]:min-w-0">
      <LinksForm initial={contact} onSaved={onSaved} />
      <PortfolioForm contact={contact} onSaved={onSaved} />
      <SyncPanel />
    </div>
  );
}

function LinksForm({ initial, onSaved }) {
  const [form, setForm] = useState({ email: initial.email, linkedin: initial.linkedin });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setMsg(null);
    try {
      const saved = await updateContact(form);
      setForm({ email: saved.email, linkedin: saved.linkedin });
      onSaved(saved);
      setMsg({ type: 'ok', text: 'Footer links saved. Blank links are hidden from visitors.' });
    } catch (err) {
      setMsg({ type: 'err', text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="border-2 border-[#3a2512] bg-[#0f0704] p-4 sm:p-6 rounded-lg space-y-4 h-fit" data-testid="admin-contact-form">
      <div className="font-osd text-[#f5a623] text-sm tracking-widest">// FOOTER CONTACT BUTTONS</div>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest flex items-center gap-2"><Mail className="w-3.5 h-3.5" /> EMAIL ADDRESS</span>
        <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
               placeholder="you@example.com" className={fieldCls} data-testid="contact-field-email" />
      </label>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest flex items-center gap-2"><Linkedin className="w-3.5 h-3.5" /> LINKEDIN PROFILE URL</span>
        <input type="text" value={form.linkedin} onChange={(e) => setForm({ ...form, linkedin: e.target.value })}
               placeholder="linkedin.com/in/your-name" className={fieldCls} data-testid="contact-field-linkedin" />
      </label>
      <Status msg={msg} testId="contact-status" />
      <button type="submit" disabled={busy} data-testid="contact-save-btn"
              className="admin-action w-full h-12 flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-60">
        <Save className="w-4 h-4" /> {busy ? 'SAVING…' : 'SAVE LINKS'}
      </button>
    </form>
  );
}

function PortfolioForm({ contact, onSaved }) {
  const [file, setFile] = useState(null);
  const [inputKey, setInputKey] = useState(0);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const has = Boolean(contact.portfolio_filename);

  const run = async (fn, okText) => {
    setBusy(true);
    setMsg(null);
    try {
      onSaved(await fn());
      setMsg({ type: 'ok', text: okText });
      setFile(null);
      setInputKey((k) => k + 1);
    } catch (err) {
      setMsg({ type: 'err', text: errText(err) });
    } finally {
      setBusy(false);
    }
  };

  const remove = () => {
    if (window.confirm('Remove your portfolio PDF from the site?')) run(removePortfolio, 'Portfolio removed. The buttons are hidden from visitors.');
  };

  return (
    <div className="border-2 border-[#3a2512] bg-[#0f0704] p-4 sm:p-6 rounded-lg space-y-4 h-fit" data-testid="admin-portfolio-form">
      <div className="font-osd text-[#f5a623] text-sm tracking-widest">// PORTFOLIO PDF <span className="text-[#8b6a3e]">· ABOUT PAGE + FOOTER</span></div>
      <div className="flex items-center gap-3 p-3 border border-[#3a2512] rounded bg-[#1a0e05]/60" data-testid="portfolio-current">
        <FileText className={`w-8 h-8 shrink-0 ${has ? 'text-[#f5a623]' : 'text-[#5a3418]'}`} />
        <div className="flex-1 min-w-0">
          <div className="font-osd text-xs text-[#ffe9b8] truncate">{has ? contact.portfolio_name || 'portfolio.pdf' : 'NO PDF UPLOADED'}</div>
          <div className="font-osd text-[10px] text-[#8b6a3e] tracking-widest">{has ? 'LIVE ON ABOUT PAGE + FOOTER' : 'BUTTONS HIDDEN UNTIL YOU UPLOAD ONE'}</div>
        </div>
        {has && (
          <a href={fileUrl(contact.portfolio_filename)} target="_blank" rel="noopener noreferrer" aria-label="Open current PDF" data-testid="portfolio-open"
             className="admin-action h-11 w-11 shrink-0 border-2 border-[#5a3418] rounded-md flex items-center justify-center text-[#ffe9b8] hover:border-[#f5a623]">
            <ExternalLink className="w-4 h-4" />
          </a>
        )}
        {has && <ActionBtn onClick={remove} disabled={busy} label="Remove PDF" tone="danger" testId="portfolio-remove"><Trash2 className="w-4 h-4" /></ActionBtn>}
      </div>
      <label className="block">
        <span className="font-osd text-xs text-[#a88552] tracking-widest">{has ? 'REPLACE WITH A NEW PDF' : 'CHOOSE PDF'}</span>
        <input key={inputKey} type="file" accept=".pdf,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="portfolio-file"
               className="mt-1 w-full font-osd text-xs text-[#d8b887] file:mr-3 file:py-2.5 file:px-3 file:border file:border-[#5a3418] file:bg-[#1a0e05] file:text-[#f5a623] file:font-osd file:text-xs file:cursor-pointer file:rounded" />
      </label>
      <Status msg={msg} testId="portfolio-status" />
      <button type="button" onClick={() => run(() => uploadPortfolio(file), 'Portfolio PDF is live.')} disabled={!file || busy} data-testid="portfolio-upload-btn"
              className="admin-action w-full h-12 flex items-center justify-center gap-2 bg-[#f5a623] text-[#1a0e05] font-osd tracking-widest px-5 rounded border-2 border-[#1a0e05] shadow-[3px_3px_0_#1a0e05] hover:bg-[#ffc968] transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
        <Upload className="w-4 h-4" /> {busy ? 'TRANSMITTING…' : 'UPLOAD PDF'}
      </button>
    </div>
  );
}
